(ns ncr.reports.explain-model
  (:require
   [clojure.core.match :refer [match]]
   [ncr.reports.common :as r-common]))

(defn- fragment-id [context]
  (->> context keys (filter string?) sort first))

(defn- r-fragments [{:keys [fragments sign] :as meta} parts]
  (->> parts
    (map
      (fn [{:keys [value context]}]
        (let [fragment-id (fragment-id context)]
          {:type :data
           :month (-> context :month first)
           :meta meta
           :fragment
           (get fragments fragment-id)
           :value
           (sign value)})))
    (sort-by
      (juxt :month (comp :name :fragment)))))

(defn- r-fragments-stack [{:keys [label sign column] :as meta} stack]
  (match stack
    {:etype :stack :parts parts :value value}
    (when (some? value)
      (concat
        [{:type :group :label label}]
        (if-not (= column :total)
          (r-fragments meta parts)
          (->> parts
            (group-by (comp first :month :context))
            (sort-by first)
            (map
              (fn [[fragment-month parts]]
                {:type :data
                 :name (get r-common/months-long (dec fragment-month))
                 :value (->> parts (map :value) (filter some?) (reduce (fn [^js a e] (.plus a e))) sign)
                 :sub-rows
                 (r-fragments meta parts)}))))
        [{:type :summary :value (sign value)}]))))

(defn r-income-stack [{:keys [kind] :as meta} stack]
  (r-fragments-stack
    (merge meta
      {:label (str (if (= :estimated kind) "Estimated" "Direct") " Income") :sign identity}) stack))

(defn r-expense-stack [{:keys [kind] :as meta} stack]
  (r-fragments-stack
    (merge meta
      {:label (str (if (= :estimated kind) "Estimated" "Direct") " Expense")
       :sign (fn [^js el] (.negated el))}) stack))

(defn r-time-stack [meta {:keys [label sign]} stack]
  (let [sign (or sign identity)]
    (match stack
      {:etype :stack :parts parts :value value}
      (when (seq parts)
        (concat
          [{:type :group :label label}]
          (->> parts
            (map
              (fn [part]
                (match part
                  {:role label' :value value' :roles
                   {:invoices
                    [:*
                     {:value total
                      :parts invoices}]
                    :ktu
                    [:*
                     {:value ktu
                      :roles
                      {:time/spent [:* {:value spent-time :parts spent-entries}]
                       :time/total [:/ {:value total-time :parts total-entries}]}}]}}
                  {:type :data
                   :name (get-in meta [:labels label'])
                   :value (sign value')
                   :project-time spent-time
                   :team-total total
                   :team-time total-time
                   :ktu ktu
                   :sub-rows
                   (concat
                     (letfn
                       [(transform [value-key entries]
                          (->> entries
                            (map
                              (fn [{:keys [value context]}]
                                [(if (= value-key :team-total)
                                   (fragment-id context)
                                   (:id context))
                                 {:type :data
                                  :meta meta
                                  value-key value
                                  :fragment
                                  (let [fragment-id (fragment-id context)]
                                    (get-in meta [:fragments fragment-id]))
                                  :month
                                  (when-let [month (get context :month)]
                                    (first month))}]))
                            (into {})))]
                       (let [sort (partial sort-by (juxt :month (comp :name :fragment)))]
                         (concat
                           (->>
                             (merge-with merge
                               (transform :team-time total-entries)
                               (transform :project-time spent-entries))
                             (vals)
                             (sort))
                           (->> invoices
                             (transform :team-total)
                             (vals)
                             (sort)))))
                     [{:type :summary
                       :value (sign value')
                       :project-time spent-time
                       :team-total total
                       :team-time total-time
                       :ktu ktu}])})))
            (sort-by :name)
            (interpose {:type :spacer})))))))

(defn- r-total [_ value]
  (match value
    {:value total}
    [{:type :group :label "Total"}
     {:type :summary :value total}]))

(defn- r-explain-estimated [meta value]
  (match value
    {:path [(:or :projects :teams :income :pnl) :estimated]
     :etype :form
     :roles
     {:income [:+ income]
      :expense [:- expense]}}
    (concat
      (r-income-stack meta income)
      (r-expense-stack meta expense)
      (r-total meta value))

    {:path [(:or :projects :teams :income) :estimated :income]
     :etype :stack}
    (r-income-stack meta value)

    {:path [(:or :projects :teams :expense) :estimated :expense]
     :etype :stack}
    (r-expense-stack meta value)

    _
    nil))

(defn r-explain-project [meta value]
  (let [r-time-props {:label "Team Expenses" :sign (fn [^js el] (.negated el))}]
    (match value
      {:path [:projects :actual]
       :etype :form
       :roles
       {:income [:+ income-direct]
        :expense
        [:-
         {:role :expense :etype :form
          :roles
          {:direct [:+ expense-direct]
           :teams [:+ expense-teams]}}]}}
      (concat
        (r-income-stack meta income-direct)
        (r-expense-stack meta expense-direct)
        (r-time-stack meta r-time-props expense-teams)
        (r-total meta value))

      {:path [:projects :actual :income]}
      (r-income-stack meta value)

      {:path [:projects :actual :expense]
       :roles
       {:direct [:+ expense-direct]
        :teams [:+ expense-teams]}}
      (concat
        (r-expense-stack meta expense-direct)
        (r-time-stack meta r-time-props expense-teams)
        (r-total meta value))

      _
      (r-explain-estimated meta value))))

(defn r-explain-team [meta value]
  (let
    [r-time-props
     {:label "Project Income"}]

    (match value
      {:path [:teams :actual]
       :roles
       {:income
        [:+
         {:roles
          {:direct [:+ income-direct]
           :projects [:+ income-projects]}}]
        :expense [:- expense-direct]}}
      (concat
        (r-time-stack meta r-time-props income-projects)
        (r-income-stack meta income-direct)
        (r-expense-stack meta expense-direct)
        (r-total meta value))

      {:path [:teams :actual :income]
       :roles
       {:direct [:+ income-direct]
        :projects [:+ income-projects]}}
      (concat
        (r-time-stack meta r-time-props income-projects)
        (r-income-stack meta income-direct)
        (r-total meta value))

      {:path [:teams :actual :expense]}
      (r-expense-stack meta value)

      _
      (r-explain-estimated meta value))))

(defn r-explain-plain [meta value]
  (match value
    {:path [:pnl :actual]
     :roles
     {:income [:+ income]
      :expense [:- expense]}}
    (concat
      (r-income-stack meta income)
      (r-expense-stack meta expense))

    {:path [:income :actual :income]}
    (r-income-stack meta value)

    {:path [:expense :actual :expense]}
    (r-expense-stack meta value)

    _
    (r-explain-estimated meta value)))
